import proxy from "api/proxy";
import dates from "tools/dates";
import func from "tools/functions";

function getLine(sequence, product, unit_price, taxes, origin) {
  const storeRec = {
    sequence: sequence,
    quantity: 1,
    unit: product.sale_uom,
    product: product.id,
    unit_price: unit_price?.toString() ?? 0,
    base_price: unit_price?.toString() ?? 0,
    taxes: [["add", taxes]],
    origin: `appointment_scheduling,${origin}`,
  };
  return storeRec;
}

async function prepareLines(event) {
  const lines = [];
  const relatedEventIds = [event.id];
  const product = event["product."];
  // Caso: producto tipo "session" (requiere crear varias citas)
  const taxes =
    product?.["template."]?.["account_category."]?.customer_taxes ??
    product?.template?.account_category?.customer_taxes ??
    [];

  if (product.type_mix === "session" && product.quantity_mix_required > 1) {
    const sessionCount = product.quantity_mix_required;
    const unitPrice = (product.list_price / sessionCount).toFixed(4);

    // 4.1 Crear registros adicionales de cita
    const sessionRecords = Array.from({ length: sessionCount - 1 }, () => ({
      party: event.party,
      professional: event.professional,
      consumer: event.consumer,
      product: event.product,
      state: "draft",
    }));

    const { data: newAppointments } = await proxy.create(
      "appointment_scheduling",
      sessionRecords,
    );

    relatedEventIds.push(...(newAppointments ?? []));

    // 4.2 Crear líneas para cada sesión
    for (let i = 0; i < sessionCount; i++) {
      const eventId = i > 0 ? newAppointments?.[i - 1] : event.id;
      lines.push(getLine(i + 1, product, unitPrice, taxes, eventId));
    }
  } else {
    // Caso: producto normal (una sola línea)
    lines.push(getLine(1, product, product.list_price, taxes, event.id));
  }
  return { lines, relatedEventIds };
}

async function linkSale(relatedEventIds, saleId) {
  await proxy.save({
    model: "appointment_scheduling",
    ids: relatedEventIds,
    values: { sale: saleId },
  });
}
async function searchSaleOpen(event) {
  const partyId =
    event["customer."]?.id ??
    event.customer?.id ??
    event.customer ??
    event["patient."]?.id ??
    event.patient?.id ??
    event.patient;

  if (!partyId) return null;

  const { data } = await proxy.search(
    "sale.sale",
    [
      ["party", "=", partyId],
      ["party.id_number", "!=", "222222222222"],
      ["state", "=", "draft"],
    ],
    ["id", "rec_name", "lines.product.name", "lines.product.time_session"],
  );

  if (data?.length > 0) {
    return data[0];
  }
}

async function createSale(event) {
  const context = proxy.getSession();
  const shop = context.shop;

  if (!event.sale) {
    const saleOpen = await searchSaleOpen(event);
    if (saleOpen) {
      const newEvent = { ...event, sale: saleOpen.id };
      event.sale = saleOpen.id;
      await proxy.saveQuery({
        model: "appointment_scheduling",
        storeRec: { id: event.id, sale: saleOpen.id },
      });
      writeSale(newEvent);
      return;
    }
  }

  // --- 1  Obtener terminal de venta asociado al usuario ---
  const { data: users } = await proxy.search(
    "res.user",
    [["id", "=", context.user]],
    ["sale_device.name"],
  );
  const saleDevice = users?.[0]?.["sale_device."] ?? null;

  // --- 2 Obtener party y dirección ---
  const partyId =
    event["customer."]?.id ??
    event.customer?.id ??
    event.customer ??
    event["patient."]?.id ??
    event.patient?.id ??
    event.patient;

  const { data: partyData } = await proxy.search(
    "party.party",
    [["id", "=", partyId]],
    ["addresses", "addresses.name", "name"],
  );
  const party = partyData?.[0];
  const address = party?.["addresses"]?.[0] ?? null;

  // --- 3 Preparar información base de la venta ---
  const saleDate = dates.dateToday();

  const saleRecord = {
    party: party?.id,
    sale_date: saleDate,
    shop,
    sale_device: saleDevice,
    invoice_address: address,
    shipment_address: address,
    invoice_type: "1",
    origin: `appointment_scheduling,${event.id}`,
    kind: "take_away",
  };

  // --- 4 Preparar líneas y eventos asociados ---
  const { lines, relatedEventIds } = await prepareLines(event);

  // --- 5 Crear la venta ---
  if (lines.length === 0) return null;

  saleRecord.lines = [["create", lines]];

  const { data: sales } = await proxy.create(
    "sale.sale",
    func.recToTryton(saleRecord),
  );

  if (!sales?.length) return null;

  const saleId = sales[0];

  // --- 6 Completar proceso: asignar número y vincular citas ---
  await proxy.buttonMethod({
    model: "sale.sale",
    method: "set_number",
    ids: [saleId],
  });
  await linkSale(relatedEventIds, saleId);
  return saleId;
}

async function writeSale(event) {
  const { lines, relatedEventIds } = await prepareLines(event);
  const saleId = typeof event.sale === "number" ? event.sale : event.sale.id;
  await linkSale(relatedEventIds, saleId);
  await proxy.saveQuery({
    model: "sale.sale",
    storeRec: {
      id: saleId,
      lines: [["create", lines]],
    },
  });
}

async function generateInvoice(recordId) {
  const sale = { sale_id: recordId };
  const argSaleDone = {
    model: "sale.sale",
    method: "faster_process",
    args: [sale],
  };
  const { data: dataPost } = await proxy.methodCall(argSaleDone);
  const argSalePost = {
    model: "sale.sale",
    method: "faster_post_invoice",
    args: [sale],
  };

  const { data: dataRec } = await proxy.methodCall(argSalePost);
  const argSaleReconcile = {
    model: "sale.sale",
    method: "reconcile_invoice",
    args: [sale],
  };

  const { dataReconcile } = await proxy.methodCall(argSaleReconcile);
  return dataReconcile;
}

export { generateInvoice, createSale, writeSale, searchSaleOpen };
